import { cx } from '@orn/shared-web/utils/cx';
import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';

import InspectLink from '../../components/InspectLink';
import Table from '../../components/Table';

import { Absent, Field, FieldList, Timestamp } from './AccountFields';
import { NameOrId } from './AccountLinks';
import { formatAccountKind, formatConnectionHealth, formatConsentedProducts } from './accountsView';

import type { AdminGetFamilyAccountQuery } from './__generated__/AccountsScene.gql';
import type { ConnectionHealthTone } from './accountsView';

type BankConnection = NonNullable<AdminGetFamilyAccountQuery['adminGetFamilyAccount']>['bankConnections'][number];

const HEALTH_TONE_CLASS: Record<ConnectionHealthTone, string> = {
  error: 'bg-error-primary text-fg-error-primary',
  warning: 'bg-warning-primary text-fg-warning-primary',
  success: 'bg-success-primary text-fg-success-primary',
};

interface IBankConnectionsPanelProps {
  bankConnections: BankConnection[];
}

/**
 * The household's bank connections, oldest first, each with the accounts
 * under it.
 *
 * Reports only what is stored. Whether Plaid itself still accepts the login is
 * a live question the Bank connections tool answers; this panel says what the
 * household has been told and when each connection last synced.
 */
const BankConnectionsPanel = ({ bankConnections }: IBankConnectionsPanelProps) => (
  <section className="flex min-w-0 flex-col gap-3">
    {bankConnections.length === 0 ? (
      <div className="text-fg-tertiary text-xs italic">No bank connections on record.</div>
    ) : (
      <div className="flex flex-col gap-5">
        {bankConnections.map((connection) => (
          <ConnectionBlock key={connection.id} connection={connection} />
        ))}
      </div>
    )}
  </section>
);

interface IConnectionBlockProps {
  connection: BankConnection;
}

const ConnectionBlock = ({ connection }: IConnectionBlockProps) => {
  const health = formatConnectionHealth(connection);
  const lastSyncedAt = connection.lastSyncedAt ?? null;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-baseline gap-2">
        <span className="text-fg-primary text-sm font-semibold">
          <NameOrId name={connection.institutionName} />
        </span>
        <span
          className={cx(
            'whitespace-nowrap px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
            HEALTH_TONE_CLASS[health.tone]
          )}
        >
          {health.label}
        </span>
      </div>

      <FieldList>
        <Field label="Linked by">
          <NameOrId name={connection.linkedByName} />
        </Field>
        <Field label="Linked">
          <Timestamp instant={connection.linkedAt} />
        </Field>
        <Field label="Last synced">
          {lastSyncedAt === null ? <Absent>Never</Absent> : <Timestamp instant={lastSyncedAt} />}
        </Field>
        <Field label="History">{connection.historicalSyncComplete ? 'Complete' : <Absent>Still loading</Absent>}</Field>
        <Field label="Products">
          {formatConsentedProducts(connection.consentedProducts) ?? <Absent>Never read from Plaid</Absent>}
        </Field>
        <Field label="Plaid item" mono>
          <InspectLink id={connection.plaidItemId} />
        </Field>
        <Field label="Connection id" mono>
          <InspectLink id={connection.id} />
        </Field>
      </FieldList>

      {connection.accounts.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No accounts under this connection.</div>
      ) : (
        <Table className="table-fixed">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Account</Table.HeaderCell>
              <Table.HeaderCell className="w-[12rem]">Kind</Table.HeaderCell>
              <Table.HeaderCell className="w-[8rem]">Visibility</Table.HeaderCell>
              <Table.HeaderCell className="w-[5.5rem]">Id</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {connection.accounts.map((account) => {
              const hiddenAt = account.hiddenAt ?? null;
              return (
                <Table.Row key={account.id}>
                  <Table.Cell className={cx('truncate', hiddenAt !== null && 'text-fg-tertiary')} title={account.name}>
                    {account.name}
                    {account.mask && <span className="text-fg-tertiary font-mono"> ···{account.mask}</span>}
                  </Table.Cell>
                  <Table.Cell muted className="truncate">
                    {formatAccountKind(account.type, account.subtype)}
                  </Table.Cell>
                  <Table.Cell muted className="truncate">
                    {hiddenAt === null ? (
                      'Shown'
                    ) : (
                      <span title={`Hidden ${formatPacificDateTime(hiddenAt)}`}>Hidden</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-xs">
                    <InspectLink id={account.id} short />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </div>
  );
};

export default BankConnectionsPanel;
